"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Button from "./Button";

/**
 * Informational notice. Infinity City only uses strictly necessary cookies plus
 * Cloudflare Web Analytics, which is cookieless and stores nothing on the
 * device (so § 25 TDDDG consent is not triggered). There is therefore nothing
 * to opt into — the banner just informs and is dismissed with a single
 * acknowledgement. The dismissal is remembered in a first-party cookie so it
 * does not reappear.
 */
const NOTICE_COOKIE = "ic_notice";
const ONE_YEAR = 60 * 60 * 24 * 365;

function alreadySeen() {
  if (typeof document === "undefined") return true;
  return document.cookie
    .split("; ")
    .some((row) => row.startsWith(`${NOTICE_COOKIE}=`));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  // Read on the client only, so SSR and first render agree.
  useEffect(() => setOpen(!alreadySeen()), []);

  const acknowledge = () => {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${NOTICE_COOKIE}=1; path=/; max-age=${ONE_YEAR}; SameSite=Lax${secure}`;
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="region"
      aria-label="Hinweis zu Cookies und Analyse"
      className="fixed inset-x-0 bottom-0 z-[70] section-pad pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto max-w-page">
        <div className="rounded-2xl border border-surface-border bg-surface-raised/95 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-display text-lg font-bold tracking-tight">
                Cookies & Analyse bei Infinity City
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-400">
                Wir setzen nur notwendige Cookies für Sicherheit und
                Grundfunktionen. Zur anonymen Reichweitenmessung nutzen wir
                Cloudflare Web Analytics — ganz ohne Cookies und ohne Tracking.
                Details findest du in den{" "}
                <Link
                  href="/cookie-einstellungen"
                  className="text-purple-300 underline-offset-2 hover:underline"
                >
                  Cookie-Hinweisen
                </Link>{" "}
                und in der{" "}
                <Link
                  href="/datenschutz"
                  className="text-purple-300 underline-offset-2 hover:underline"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
            <div className="flex items-center lg:shrink-0">
              <Button withArrow={false} onClick={acknowledge}>
                Verstanden
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
